/*
 * Eataly cart — pure helpers shared by the cart widget (eataly-cart-timeslots.js)
 * and the node tests in theme/tests/eataly-cart.test.js.
 *
 * Everything here is side-effect free and DOM-free except initGiftNoteValidation,
 * which is called once at the bottom when running in a browser.
 */
(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.EatalyCart = api;

    if (root.document) {
      api.initGiftNoteValidation(root.document);
    }
  }
})(typeof window !== 'undefined' ? window : null, function () {
  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // The store is in Toronto. Availability payloads carry their own `timezone`
  // when the backend knows it; this is only the fallback.
  var DEFAULT_TIME_ZONE = 'America/Toronto';
  var DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

  function isDayKey(value) {
    return typeof value === 'string' && DAY_KEY.test(value);
  }

  function str(value) {
    return value === null || value === undefined ? '' : String(value);
  }

  /** Calendar day ("YYYY-MM-DD") that `date` falls on in `timeZone`. */
  function dayKeyInZone(date, timeZone) {
    try {
      // en-CA formats as YYYY-MM-DD, which is exactly the key shape the
      // availability API uses for `window.date`.
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone || DEFAULT_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    } catch (_error) {
      // ponytail: no Intl time zone support -> fall back to the viewer's own
      // local day. Wrong only for customers browsing from another time zone on
      // an ancient engine; everything downstream still renders a real date.
      var year = date.getFullYear();
      var month = date.getMonth() + 1;
      var day = date.getDate();
      return year + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
    }
  }

  /** Shift a day key by whole days. UTC arithmetic, so DST never moves it. */
  function shiftDayKey(dayKey, days) {
    if (!isDayKey(dayKey)) {
      return null;
    }

    var parts = dayKey.split('-');
    var date = new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10)));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  /**
   * Human label for a slot date, relative to "now" in the store's time zone.
   * Returns null for anything that is not a real day key.
   *
   * { label: 'Tomorrow, October 1', relative: 'Tomorrow', weekday: 'Thursday', ... }
   */
  function formatSlotDate(dayKey, options) {
    if (!isDayKey(dayKey)) {
      return null;
    }

    options = options || {};

    var timeZone = options.timeZone || DEFAULT_TIME_ZONE;
    var now = options.now || new Date();
    var today = dayKeyInZone(now, timeZone);
    var parts = dayKey.split('-');
    var monthIndex = parseInt(parts[1], 10) - 1;
    var dayNumber = parseInt(parts[2], 10);
    var weekday = WEEKDAYS[new Date(Date.UTC(parseInt(parts[0], 10), monthIndex, dayNumber)).getUTCDay()];
    var dateLabel = MONTHS[monthIndex] + ' ' + dayNumber;
    var relative = null;

    if (dayKey === today) {
      relative = 'Today';
    } else if (dayKey === shiftDayKey(today, 1)) {
      relative = 'Tomorrow';
    }

    return {
      dayKey: dayKey,
      relative: relative,
      weekday: weekday,
      dateLabel: dateLabel,
      label: (relative || weekday) + ', ' + dateLabel,
      isPast: dayKey < today
    };
  }

  function startMinutes(serviceWindow) {
    var value = serviceWindow && serviceWindow.collection_time;
    return typeof value === 'number' && isFinite(value) ? value : 0;
  }

  /**
   * Earliest bookable window in the list, using the same ordering the calendar
   * uses (date, then collection time, then name). Returns null for an empty or
   * unusable list — callers must not claim "first available" without one.
   */
  function firstAvailableWindow(windows) {
    if (!windows || !windows.length) {
      return null;
    }

    var best = null;

    for (var i = 0; i < windows.length; i++) {
      var candidate = windows[i];

      if (!candidate || !isDayKey(candidate.date) || !candidate.code) {
        continue;
      }

      if (best === null) {
        best = candidate;
        continue;
      }

      if (candidate.date !== best.date) {
        if (candidate.date < best.date) {
          best = candidate;
        }
        continue;
      }

      if (startMinutes(candidate) !== startMinutes(best)) {
        if (startMinutes(candidate) < startMinutes(best)) {
          best = candidate;
        }
        continue;
      }

      if (str(candidate.name).localeCompare(str(best.name)) < 0) {
        best = candidate;
      }
    }

    return best;
  }

  /**
   * Resolve a slot code against a freshly fetched availability list.
   * Returns the live window object, or null when the code no longer exists —
   * which is the only signal allowed to authorise persisting a slot.
   */
  function validateSlot(code, windows) {
    if (!code || !windows || !windows.length) {
      return null;
    }

    for (var i = 0; i < windows.length; i++) {
      if (windows[i] && windows[i].code === code) {
        return windows[i];
      }
    }

    return null;
  }

  /**
   * The whole confirm decision in one place: a staged selection is only ever
   * persisted when freshly fetched availability still offers that exact code,
   * and what gets persisted is the *live* window, not the staged copy the
   * customer clicked. Cancelling simply never calls this.
   */
  function resolveSlotConfirmation(staged, freshWindows) {
    if (!staged || !staged.code) {
      return { action: 'reject', reason: 'no-selection' };
    }

    var live = validateSlot(staged.code, freshWindows);

    if (!live) {
      return { action: 'reject', reason: 'unavailable' };
    }

    return { action: 'persist', window: live };
  }

  /** Cart attribute keys a slot writes. Must match the widget's constants. */
  var SLOT_DATE_KEY = 'Date';
  var SLOT_CODE_KEY = 'code';
  var SLOT_NAME_KEY = 'window';

  /** Attribute payload for a chosen slot, or the three nulls that clear one. */
  function slotAttributes(serviceWindow) {
    var attributes = {};

    attributes[SLOT_DATE_KEY] = serviceWindow ? serviceWindow.date : null;
    attributes[SLOT_CODE_KEY] = serviceWindow ? serviceWindow.code : null;
    attributes[SLOT_NAME_KEY] = serviceWindow ? serviceWindow.name : null;

    return attributes;
  }

  /**
   * Did Shopify actually store the slot we asked for? Checked against the cart
   * returned by /cart/update.js, so a 200 that didn't take is still a failure.
   */
  function slotAttributesMatch(cartAttributes, serviceWindow) {
    if (!serviceWindow) {
      return false;
    }

    var attributes = cartAttributes || {};

    return str(attributes[SLOT_CODE_KEY]) === str(serviceWindow.code) &&
      str(attributes[SLOT_DATE_KEY]) === str(serviceWindow.date) &&
      str(attributes[SLOT_NAME_KEY]) === str(serviceWindow.name);
  }

  /** True once the cart carries no slot at all. */
  function slotAttributesCleared(cartAttributes) {
    var attributes = cartAttributes || {};

    return !attributes[SLOT_CODE_KEY] && !attributes[SLOT_DATE_KEY] && !attributes[SLOT_NAME_KEY];
  }

  /**
   * Serialises cart writes so two in-flight saves can never land out of order
   * and clobber the newer text. Tasks run one at a time, in the order they were
   * queued, and a rejected task does not stall the ones behind it.
   *
   * Tasks are called (not just awaited) at their turn, so a task that reads
   * current state when it runs naturally collapses into a no-op if an earlier
   * task already saved that state.
   */
  function createWriteQueue() {
    var tail = Promise.resolve();

    return function enqueue(task) {
      var next = tail.then(task, task);

      // The chain itself must never stay rejected, or everything behind a
      // single failure would be skipped.
      tail = next.then(null, function () {});

      return next;
    };
  }

  /**
   * Minimal /cart/update.js body for the notes panel: only the fields that
   * actually changed. Returns null when nothing needs saving, so checkout can
   * submit straight through.
   */
  function buildNotesPayload(current, saved) {
    current = current || {};
    saved = saved || {};

    var payload = null;
    var attributes = {};
    var hasAttributes = false;

    if (str(current.note) !== str(saved.note)) {
      payload = payload || {};
      payload.note = str(current.note);
    }

    if (str(current.deliveryNote) !== str(saved.deliveryNote)) {
      attributes['Delivery Note'] = str(current.deliveryNote);
      hasAttributes = true;
    }

    if (str(current.substitutions) !== str(saved.substitutions)) {
      attributes.Substitutions = str(current.substitutions);
      hasAttributes = true;
    }

    if (hasAttributes) {
      payload = payload || {};
      payload.attributes = attributes;
    }

    return payload;
  }

  function linePrice(item) {
    if (!item) {
      return 0;
    }

    if (typeof item.final_line_price === 'number') {
      return item.final_line_price;
    }

    return typeof item.line_price === 'number' ? item.line_price : 0;
  }

  /**
   * Summary arithmetic, in cents, from a /cart.js payload.
   *
   * `total_price` is the single source of truth: it already has every discount
   * applied and already includes the refundable hold line. Products is derived
   * by subtraction, so products + hold === estimatedTotal always holds and no
   * discount is ever subtracted twice.
   */
  function summaryTotals(cart, holdHandle) {
    var items = (cart && cart.items) || [];
    var hold = 0;

    for (var i = 0; i < items.length; i++) {
      if (holdHandle && items[i] && items[i].handle === holdHandle) {
        hold += linePrice(items[i]);
      }
    }

    var estimatedTotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;

    return {
      products: estimatedTotal - hold,
      hold: hold,
      estimatedTotal: estimatedTotal
    };
  }

  function withCommas(value) {
    return value.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  }

  /**
   * Render cents using a Shopify money format string (shop.money_format), so
   * JS-updated amounts match the Liquid-rendered ones character for character.
   */
  function formatMoney(cents, format) {
    var amount = typeof cents === 'number' && isFinite(cents) ? cents : 0;
    var template = str(format) || '${{amount}}';

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount_no_decimals':
          return withCommas(String(Math.round(amount / 100)));
        case 'amount_with_comma_separator':
          return withCommas((amount / 100).toFixed(2)).replace(/,/g, '\u0000').replace('.', ',').replace(/\u0000/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return String(Math.round(amount / 100)).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
        default:
          return withCommas((amount / 100).toFixed(2));
      }
    });
  }

  /**
   * The alcohol / food gate, resolved from the inert JSON that the cart section
   * renders (`[data-eataly-food-policy]`):
   *
   *   {"enabled":true,"lines":{"<line key>":"alcohol"|"food"|"other"}}
   *
   * Takes the element or its text, so callers can hand over whatever they just
   * read out of the DOM. Unparseable, missing or empty JSON resolves to "not
   * enabled, nothing to fix", which leaves checkout exactly as it was.
   *
   * Call this on every render rather than caching the result: the theme
   * replaces the whole cart section on any cart change, so the JSON in the DOM
   * is always the current classification and a cached copy would go stale.
   */
  function resolveFoodPolicy(source) {
    var raw = typeof source === 'string' ? source : source && source.textContent;
    var parsed = null;

    try {
      parsed = JSON.parse(str(raw));
    } catch (_error) {
      parsed = null;
    }

    var lines = parsed && parsed.lines && typeof parsed.lines === 'object' ? parsed.lines : {};
    var enabled = !!parsed && parsed.enabled === true;
    var alcohol = false;
    var food = false;

    Object.keys(lines).forEach(function (key) {
      if (lines[key] === 'alcohol') {
        alcohol = true;
      } else if (lines[key] === 'food') {
        food = true;
      }
    });

    return {
      enabled: enabled,
      alcohol: alcohol,
      food: food,
      needsFood: enabled && alcohol && !food
    };
  }

  /**
   * The gift rows add to cart through the theme's AJAX handler, which calls
   * preventDefault on the click and so never triggers native form validation.
   * This capture-phase listener restores it for the one field that needs it:
   * the gift note Content property, which is useless when blank.
   */
  function initGiftNoteValidation(doc) {
    if (!doc || !doc.addEventListener || doc.__eatalyGiftNoteValidation) {
      return;
    }

    doc.__eatalyGiftNoteValidation = true;

    doc.addEventListener('click', function (event) {
      var target = event.target;

      if (!target || !target.closest) {
        return;
      }

      var button = target.closest('.eataly-gift [data-action="add-to-cart"]');

      if (!button) {
        return;
      }

      var form = button.closest('form');
      var field = form && form.querySelector('[data-eataly-gift-note]');

      if (!field) {
        return;
      }

      var error = form.querySelector('[data-eataly-gift-error]');

      if (field.value.trim() === '') {
        // Capture phase: stops the theme's delegated add-to-cart handler too.
        event.preventDefault();
        event.stopPropagation();

        if (error) {
          error.hidden = false;
        }

        field.setAttribute('aria-invalid', 'true');
        field.focus();
        return;
      }

      if (error) {
        error.hidden = true;
      }

      field.removeAttribute('aria-invalid');
    }, true);
  }

  return {
    DEFAULT_TIME_ZONE: DEFAULT_TIME_ZONE,
    isDayKey: isDayKey,
    dayKeyInZone: dayKeyInZone,
    shiftDayKey: shiftDayKey,
    formatSlotDate: formatSlotDate,
    firstAvailableWindow: firstAvailableWindow,
    validateSlot: validateSlot,
    resolveSlotConfirmation: resolveSlotConfirmation,
    slotAttributes: slotAttributes,
    slotAttributesMatch: slotAttributesMatch,
    slotAttributesCleared: slotAttributesCleared,
    createWriteQueue: createWriteQueue,
    buildNotesPayload: buildNotesPayload,
    summaryTotals: summaryTotals,
    formatMoney: formatMoney,
    resolveFoodPolicy: resolveFoodPolicy,
    initGiftNoteValidation: initGiftNoteValidation
  };
});
